'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { ChevronLeft, ChevronRight, Smile } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

import NextImage from '@/components/NextImage'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'

import emojiService from '@/services/emoji.service'

const emojiCategories = {
  smileys: {
    name: 'Smileys & People',
    icon: '😀',
    emojis: [
      '😀',
      '😃',
      '😄',
      '😁',
      '😆',
      '😅',
      '😂',
      '🤣',
      '😊',
      '😇',
      '🙂',
      '🙃',
      '😉',
      '😌',
      '😍',
      '🥰',
      '😘',
      '😗',
      '😙',
      '😚',
      '😋',
      '😛',
      '😝',
      '😜',
      '🤪',
      '🤨',
      '🧐',
      '🤓',
      '😎',
      '🥸',
      '🤩',
      '🥳',
      '😏',
      '😒',
      '😞',
      '😔',
      '😟',
      '😕',
      '🙁',
      '☹️',
      '😣',
      '😖',
      '😫',
      '😩',
      '🥺',
      '😢',
      '😭',
      '😤',
      '😠',
      '😡',
      '🤬',
      '🤯',
      '😳',
      '🥵',
      '🥶',
      '😱',
      '😨',
      '😰',
      '😥',
      '😓',
      '🤗',
      '🤔',
      '🤭',
      '🤫',
      '🤥',
      '😶',
      '😐',
      '😑',
      '😬',
      '🙄',
      '😯',
      '😦',
      '😧',
      '😮',
      '😲',
      '🥱',
      '😴',
      '🤤',
      '😪',
      '😵',
      '🤐',
      '🥴',
      '🤢',
      '🤮',
      '🤧',
      '😷',
      '🤒',
      '🤕',
      '🤑',
      '🤠',
    ],
  },
  animals: {
    name: 'Animals & Nature',
    icon: '🐶',
    emojis: [
      '🐶',
      '🐱',
      '🐭',
      '🐹',
      '🐰',
      '🦊',
      '🐻',
      '🐼',
      '🐨',
      '🐯',
      '🦁',
      '🐮',
      '🐷',
      '🐽',
      '🐸',
      '🐵',
      '🙈',
      '🙉',
      '🙊',
      '🐒',
      '🐔',
      '🐧',
      '🐦',
      '🐤',
      '🐣',
      '🐥',
      '🦆',
      '🦅',
      '🦉',
      '🦇',
      '🐺',
      '🐗',
      '🐴',
      '🦄',
      '🐝',
      '🐛',
      '🦋',
      '🐌',
      '🐞',
      '🐜',
      '🦟',
      '🦗',
      '🕷️',
      '🕸️',
      '🦂',
      '🐢',
      '🐍',
      '🦎',
      '🦖',
      '🦕',
      '🐙',
      '🦑',
      '🦐',
      '🦞',
      '🦀',
      '🐡',
      '🐠',
      '🐟',
      '🐬',
      '🐳',
      '🐋',
      '🦈',
      '🐊',
      '🐅',
      '🐆',
      '🦓',
      '🦍',
      '🦧',
      '🐘',
      '🦛',
      '🦏',
      '🐪',
      '🐫',
      '🦒',
      '🦘',
      '🐃',
      '🐂',
      '🐄',
      '🐎',
      '🐖',
    ],
  },
  food: {
    name: 'Food & Drink',
    icon: '🍎',
    emojis: [
      '🍎',
      '🍐',
      '🍊',
      '🍋',
      '🍌',
      '🍉',
      '🍇',
      '🍓',
      '🫐',
      '🍈',
      '🍒',
      '🍑',
      '🥭',
      '🍍',
      '🥥',
      '🥝',
      '🍅',
      '🍆',
      '🥑',
      '🥦',
      '🥬',
      '🥒',
      '🌶️',
      '🫑',
      '🌽',
      '🥕',
      '🫒',
      '🧄',
      '🧅',
      '🥔',
      '🍠',
      '🥐',
      '🥖',
      '🍞',
      '🥨',
      '🥯',
      '🧀',
      '🥚',
      '🍳',
      '🧈',
      '🥞',
      '🧇',
      '🥓',
      '🥩',
      '🍗',
      '🍖',
      '🦴',
      '🌭',
      '🍔',
      '🍟',
      '🍕',
      '🥪',
      '🥙',
      '🧆',
      '🌮',
      '🌯',
      '🫔',
      '🥗',
      '🥘',
      '🫕',
      '🍝',
      '🍜',
      '🍲',
      '🍛',
      '🍣',
      '🍱',
      '🥟',
      '🦪',
      '🍤',
      '🍙',
      '🍚',
      '🍘',
      '🍥',
      '🥠',
      '🥮',
      '🍢',
      '🍡',
      '🍧',
      '🍨',
      '🍦',
    ],
  },
  activities: {
    name: 'Activities',
    icon: '⚽',
    emojis: [
      '⚽',
      '🏀',
      '🏈',
      '⚾',
      '🥎',
      '🎾',
      '🏐',
      '🏉',
      '🥏',
      '🎱',
      '🪀',
      '🏓',
      '🏸',
      '🏒',
      '🏑',
      '🥍',
      '🏏',
      '🪃',
      '🥅',
      '⛳',
      '🪁',
      '🏹',
      '🎣',
      '🤿',
      '🥊',
      '🥋',
      '🎽',
      '🛹',
      '🛷',
      '⛸️',
      '🥌',
      '🎿',
      '⛷️',
      '🏂',
      '🪂',
      '🏋️',
      '🤼',
      '🤸',
      '⛹️',
      '🤺',
      '🏇',
      '🧘',
      '🏄',
      '🏊',
      '🤽',
      '🚣',
      '🧗',
      '🚵',
      '🚴',
      '🏆',
      '🥇',
      '🥈',
      '🥉',
      '🏅',
      '🎖️',
      '🏵️',
      '🎗️',
      '🎫',
      '🎟️',
      '🎪',
      '🤹',
      '🎭',
      '🩰',
      '🎨',
      '🎬',
      '🎤',
      '🎧',
      '🎼',
      '🎵',
      '🎶',
      '🥁',
      '🪘',
      '🎹',
      '🎷',
      '🎺',
      '🪗',
      '🎸',
      '🪕',
      '🎻',
      '🎲',
    ],
  },
  travel: {
    name: 'Travel & Places',
    icon: '🚗',
    emojis: [
      '🚗',
      '🚕',
      '🚙',
      '🚌',
      '🚎',
      '🏎️',
      '🚓',
      '🚑',
      '🚒',
      '🚐',
      '🛻',
      '🚚',
      '🚛',
      '🚜',
      '🏍️',
      '🛵',
      '🚲',
      '🛴',
      '🛹',
      '🛼',
      '🚁',
      '🛸',
      '✈️',
      '🛩️',
      '🪂',
      '💺',
      '🚀',
      '🛰️',
      '🚉',
      '🚞',
      '🚝',
      '🚄',
      '🚅',
      '🚈',
      '🚂',
      '🚆',
      '🚇',
      '🚊',
      '🚟',
      '🚠',
      '🚡',
      '⛴️',
      '🚢',
      '⛵',
      '🛶',
      '🚤',
      '🛥️',
      '🛳️',
      '⚓',
      '🪝',
      '⛽',
      '🚨',
      '🚥',
      '🚦',
      '🛑',
      '🚧',
      '⚱️',
      '⛱️',
      '🏖️',
      '🏝️',
      '🏜️',
      '🏔️',
      '⛰️',
      '🏕️',
      '⛺',
      '🏠',
      '🏡',
      '🏘️',
      '🏚️',
      '🏗️',
      '🏭',
      '🏢',
      '🏬',
      '🏣',
      '🏤',
      '🏥',
      '🏦',
      '🏨',
      '🏪',
      '🏫',
    ],
  },
  objects: {
    name: 'Objects',
    icon: '💡',
    emojis: [
      '💡',
      '🔦',
      '🕯️',
      '🪔',
      '🧯',
      '🛢️',
      '💸',
      '💵',
      '💴',
      '💶',
      '💷',
      '🪙',
      '💰',
      '💳',
      '💎',
      '⚖️',
      '🪜',
      '🧰',
      '🪛',
      '🔧',
      '🔨',
      '⚒️',
      '🛠️',
      '⛏️',
      '🪓',
      '🪚',
      '🔩',
      '⚙️',
      '🪤',
      '🧱',
      '⛓️',
      '🧲',
      '🔫',
      '💣',
      '🧨',
      '🪓',
      '🔪',
      '🗡️',
      '⚔️',
      '🛡️',
      '🚬',
      '⚰️',
      '🪦',
      '⚱️',
      '🏺',
      '🔮',
      '📿',
      '🧿',
      '💈',
      '⚗️',
      '🔭',
      '🔬',
      '🕳️',
      '🩹',
      '🩺',
      '💊',
      '💉',
      '🧬',
      '🦠',
      '🧫',
      '🧪',
      '🌡️',
      '🧹',
      '🪣',
      '🧴',
      '🧽',
      '🧻',
      '🪒',
      '🧼',
      '🪥',
    ],
  },
  symbols: {
    name: 'Symbols',
    icon: '❤️',
    emojis: [
      '❤️',
      '🧡',
      '💛',
      '💚',
      '💙',
      '💜',
      '🖤',
      '🤍',
      '🤎',
      '💔',
      '❣️',
      '💕',
      '💞',
      '💓',
      '💗',
      '💖',
      '💘',
      '💝',
      '💟',
      '☮️',
      '✝️',
      '☪️',
      '🕉️',
      '☸️',
      '✡️',
      '🔯',
      '🕎',
      '☯️',
      '☦️',
      '🛐',
      '⛎',
      '♈',
      '♉',
      '♊',
      '♋',
      '♌',
      '♍',
      '♎',
      '♏',
      '♐',
      '♑',
      '♒',
      '♓',
      '🆔',
      '⚛️',
      '🉑',
      '☢️',
      '☣️',
      '📴',
      '📳',
      '🈶',
      '🈚',
      '🈸',
      '🈺',
      '🈷️',
      '✴️',
      '🆚',
      '💮',
      '🉐',
      '㊙️',
      '㊗️',
      '🈴',
      '🈵',
      '🈹',
      '🈲',
      '🅰️',
      '🅱️',
      '🆎',
      '🆑',
      '🅾️',
      '🆘',
      '❌',
      '⭕',
      '🛑',
      '⛔',
      '📛',
      '🚫',
      '💯',
      '💢',
      '♨️',
    ],
  },
  flags: {
    name: 'Flags',
    icon: '🏁',
    emojis: [
      '🏁',
      '🚩',
      '🎌',
      '🏴',
      '🏳️',
      '🏳️‍🌈',
      '🏳️‍⚧️',
      '🏴‍☠️',
      '🇦🇫',
      '🇦🇽',
      '🇦🇱',
      '🇩🇿',
      '🇦🇸',
      '🇦🇩',
      '🇦🇴',
      '🇦🇮',
      '🇦🇶',
      '🇦🇬',
      '🇦🇷',
      '🇦🇲',
      '🇦🇼',
      '🇦🇺',
      '🇦🇹',
      '🇦🇿',
      '🇧🇸',
      '🇧🇭',
      '🇧🇩',
      '🇧🇧',
      '🇧🇾',
      '🇧🇪',
      '🇧🇿',
      '🇧🇯',
      '🇧🇲',
      '🇧🇹',
      '🇧🇴',
      '🇧🇦',
      '🇧🇼',
      '🇧🇷',
      '🇮🇴',
      '🇻🇬',
      '🇧🇳',
      '🇧🇬',
      '🇧🇫',
      '🇧🇮',
      '🇰🇭',
      '🇨🇲',
      '🇨🇦',
      '🇮🇨',
      '🇨🇻',
      '🇧🇶',
      '🇰🇾',
      '🇨🇫',
      '🇹🇩',
      '🇨🇱',
      '🇨🇳',
      '🇨🇽',
      '🇨🇨',
      '🇨🇴',
      '🇰🇲',
      '🇨🇬',
      '🇨🇩',
      '🇨🇰',
      '🇨🇷',
      '🇨🇮',
      '🇭🇷',
      '🇨🇺',
      '🇨🇼',
      '🇨🇾',
      '🇨🇿',
      '🇩🇰',
    ],
  },
} as const

interface MemeEmojiData {
  id: number
  name: string
  imageUrl: string
}

interface EmojiCategory {
  name: string
  icon: string | React.ReactNode
  emojis: string[]
  customEmojis?: MemeEmojiData[]
}

interface EnhancedEmojiPickerProps {
  onEmojiSelect: (emoji: string) => void
  onCustomEmojiSelect?: (emoji: MemeEmojiData) => void
  onRemoveReaction?: (emojiData: { unicode?: string; imageUrl?: string; name?: string }) => void
  onOpenChange?: (isOpen: boolean) => void
  className?: string
  buttonClassName?: string
  buttonIconClassName?: string
  showMemeCategory?: boolean
  modal?: boolean
  userReactions?: Array<{
    unicode?: string
    imageUrl?: string
    name?: string
  }>
}

function EmojiPicker({
  onEmojiSelect,
  onCustomEmojiSelect,
  onRemoveReaction,
  onOpenChange,
  className,
  buttonClassName,
  buttonIconClassName,
  showMemeCategory = true,
  modal = false,
  userReactions = [],
}: EnhancedEmojiPickerProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [customEmojis, setCustomEmojis] = React.useState<MemeEmojiData[]>([])
  const [loading, setLoading] = React.useState(false)
  const [dynamicCategories, setDynamicCategories] = React.useState<Record<string, EmojiCategory>>({})
  const [selectedCategory, setSelectedCategory] = React.useState<string>('memes')

  const categoryScrollRef = React.useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = React.useState(false)
  const [canScrollRight, setCanScrollRight] = React.useState(true)
  const isUpdatingRef = React.useRef(false)

  // Helper function to check if an emoji is reacted by the user
  const isEmojiReacted = React.useCallback(
    (emoji: MemeEmojiData) => {
      return userReactions.some(reaction => {
        if (reaction.imageUrl && emoji.imageUrl) {
          return reaction.imageUrl === emoji.imageUrl
        }
        if (reaction.name && emoji.name) {
          return reaction.name === emoji.name
        }
        return false
      })
    },
    [userReactions]
  )

  // Fetch custom emojis
  const fetchCustomEmojis = React.useCallback(async () => {
    if (!showMemeCategory) return

    try {
      setLoading(true)
      const response = await emojiService.getEmojis({
        queryParams: {
          pageNumber: 1,
          pageSize: 50,
        },
      })

      const emojis = response.data || []
      setCustomEmojis(emojis)

      return emojis
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch custom emojis:', error)
      return []
    } finally {
      setLoading(false)
    }
  }, [showMemeCategory])

  // Build dynamic categories
  React.useEffect(() => {
    const buildCategories = async () => {
      const customEmojis = await fetchCustomEmojis()

      const categories: Record<string, EmojiCategory> = {}

      // Add meme category if we have custom emojis
      if (showMemeCategory && customEmojis && customEmojis.length > 0) {
        categories.memes = {
          name: 'Memes',
          icon: '🎭', // Meme icon
          emojis: [], // No string emojis for this category
          customEmojis: customEmojis,
        }
      }

      Object.entries(emojiCategories).forEach(([key, category]) => {
        categories[key] = category as unknown as EmojiCategory
      })

      setDynamicCategories(categories)

      // Set default selected category
      if (showMemeCategory && customEmojis && customEmojis.length > 0) {
        setSelectedCategory('memes')
      } else {
        setSelectedCategory('smileys')
      }
    }

    if (isOpen) {
      buildCategories()
    }
  }, [isOpen, showMemeCategory, fetchCustomEmojis])

  const categoryKeys = Object.keys(dynamicCategories)
  const currentCategoryIndex = categoryKeys.indexOf(selectedCategory)

  // Scroll detection (same as original)
  const updateScrollButtons = React.useCallback(() => {
    if (isUpdatingRef.current || !categoryScrollRef.current) return

    const container = categoryScrollRef.current
    const { scrollLeft, scrollWidth, clientWidth } = container
    const threshold = 1
    const maxScrollLeft = scrollWidth - clientWidth

    const newCanScrollLeft = scrollLeft > threshold
    const newCanScrollRight = scrollLeft < maxScrollLeft - threshold

    setCanScrollLeft(prev => (prev !== newCanScrollLeft ? newCanScrollLeft : prev))
    setCanScrollRight(prev => (prev !== newCanScrollRight ? newCanScrollRight : prev))
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttledUpdateScrollButtons = React.useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout
      return () => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(updateScrollButtons, 16)
      }
    })(),
    [updateScrollButtons]
  )

  const scrollCategoryLeft = () => {
    if (categoryScrollRef.current && canScrollLeft) {
      isUpdatingRef.current = true
      categoryScrollRef.current.scrollBy({ left: -60, behavior: 'smooth' })
      setTimeout(() => {
        isUpdatingRef.current = false
        updateScrollButtons()
      }, 300)
    }
  }

  const scrollCategoryRight = () => {
    if (categoryScrollRef.current && canScrollRight) {
      isUpdatingRef.current = true
      categoryScrollRef.current.scrollBy({ left: 60, behavior: 'smooth' })
      setTimeout(() => {
        isUpdatingRef.current = false
        updateScrollButtons()
      }, 300)
    }
  }

  const scrollToCategory = (category: string) => {
    if (categoryScrollRef.current) {
      const categoryIndex = categoryKeys.indexOf(category)
      const categoryButton = categoryScrollRef.current.children[categoryIndex] as HTMLElement

      if (categoryButton) {
        isUpdatingRef.current = true
        categoryButton.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        })
        setTimeout(() => {
          isUpdatingRef.current = false
          updateScrollButtons()
        }, 300)
      }
    }
  }

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category)
    scrollToCategory(category)
  }

  const handleEmojiClick = (emoji: string) => {
    onEmojiSelect(emoji)
    setIsOpen(false)
  }

  const handleCustomEmojiClick = (emoji: MemeEmojiData) => {
    const isReacted = isEmojiReacted(emoji)

    if (isReacted && onRemoveReaction) {
      // Remove the reaction
      onRemoveReaction({
        imageUrl: emoji.imageUrl,
        name: emoji.name,
      })
    } else if (onCustomEmojiSelect) {
      // Add the reaction
      onCustomEmojiSelect(emoji)
    } else {
      const imgHtml = `<img class="emoji" style="width: 32px; height: 32px;" draggable="false" src="${emoji.imageUrl}" alt=":${emoji.name}:" aria-label=":${emoji.name}:" data-type="emoji" data-id="${emoji.id}" data-mce-src="${emoji.imageUrl}">`
      onEmojiSelect(imgHtml)
    }
    setIsOpen(false)
  }

  React.useEffect(() => {
    if (!isOpen || !categoryScrollRef.current) return

    const scrollContainer = categoryScrollRef.current
    const handleScroll = () => {
      if (!isUpdatingRef.current) {
        throttledUpdateScrollButtons()
      }
    }

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true })
    const timeoutId = setTimeout(() => {
      updateScrollButtons()
    }, 50)

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll)
      clearTimeout(timeoutId)
    }
  }, [isOpen, throttledUpdateScrollButtons, updateScrollButtons])

  React.useEffect(() => {
    if (isOpen) {
      const timeoutId = setTimeout(() => {
        scrollToCategory(selectedCategory)
      }, 100)
      return () => clearTimeout(timeoutId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedCategory])

  const goToPreviousCategory = () => {
    if (currentCategoryIndex > 0) {
      const newCategory = categoryKeys[currentCategoryIndex - 1]
      setSelectedCategory(newCategory)
      scrollToCategory(newCategory)
    }
  }

  const goToNextCategory = () => {
    if (currentCategoryIndex < categoryKeys.length - 1) {
      const newCategory = categoryKeys[currentCategoryIndex + 1]
      setSelectedCategory(newCategory)
      scrollToCategory(newCategory)
    }
  }

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault()
          goToPreviousCategory()
          break
        case 'ArrowRight':
          e.preventDefault()
          goToNextCategory()
          break
        case 'Escape':
          setIsOpen(false)
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentCategoryIndex])

  const renderCategoryIcon = (category: EmojiCategory, categoryKey: string) => {
    if (categoryKey === 'memes') {
      return (
        <div className="relative">
          <span className="text-base">🎭</span>
          {/* {customEmojis.length > 0 && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-xs text-white font-bold">{customEmojis.length}</span>
            </div>
          )} */}
        </div>
      )
    }
    return <span className="text-base">{category.icon}</span>
  }

  const renderEmojiGrid = () => {
    const category = dynamicCategories[selectedCategory]
    if (!category) return null

    // Render custom emojis for meme category
    if (selectedCategory === 'memes' && category.customEmojis) {
      if (loading) {
        return (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        )
      }

      return (
        <div className="grid grid-cols-6 gap-2">
          <AnimatePresence mode="wait">
            {category.customEmojis.map((emoji, index) => {
              const isReacted = isEmojiReacted(emoji)
              return (
                <motion.button
                  key={`custom-${emoji.id}`}
                  onClick={() => handleCustomEmojiClick(emoji)}
                  className={cn(
                    'aspect-square flex items-center justify-center rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 p-1 relative',
                    isReacted ? 'bg-blue-100 hover:bg-blue-200 ring-2 ring-blue-300' : 'hover:bg-gray-100'
                  )}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{
                    duration: 0.1,
                    delay: index * 0.02,
                  }}
                  title={`${emoji.name}${isReacted ? ' (Reacted)' : ''}`}
                >
                  <NextImage
                    src={emoji.imageUrl}
                    alt={emoji.name}
                    width={32}
                    height={32}
                    className="rounded object-cover"
                    unoptimized
                  />
                  {isReacted && (
                    <motion.div
                      className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full flex items-center justify-center"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                    >
                      <span className="text-white text-xs">✓</span>
                    </motion.div>
                  )}
                </motion.button>
              )
            })}
          </AnimatePresence>
        </div>
      )
    }

    return (
      <div className="grid grid-cols-8 gap-1">
        <AnimatePresence mode="wait">
          {category.emojis.map((emoji, index) => (
            <motion.button
              key={`${selectedCategory}-${emoji}-${index}`}
              onClick={() => handleEmojiClick(emoji)}
              className="aspect-square flex items-center justify-center text-lg hover:bg-gray-100 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{
                duration: 0.1,
                delay: index * 0.002,
              }}
              title={`Add ${emoji}`}
            >
              {emoji}
            </motion.button>
          ))}
        </AnimatePresence>
      </div>
    )
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    onOpenChange?.(open)
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange} modal={modal}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn('size-12 p-0 hover:bg-gray-100 transition-colors relative', buttonClassName)}
          type="button"
          title="Add emoji or meme"
        >
          <Smile className={cn('!size-6 text-gray-600', buttonIconClassName)} />
          {showMemeCategory && customEmojis.length > 0 && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full"></div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-80 p-0 z-[99999]', className)} align="start">
        <div className="flex flex-col h-96">
          {/* Category Carousel Header */}
          <div className="border-b bg-gray-50/30">
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'h-10 w-8 p-0 flex-shrink-0 transition-all duration-200',
                  canScrollLeft ? 'hover:bg-gray-200 text-gray-700' : 'opacity-30 cursor-not-allowed text-gray-400'
                )}
                onClick={scrollCategoryLeft}
                disabled={!canScrollLeft}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <div className="flex-1 overflow-hidden">
                <div
                  ref={categoryScrollRef}
                  className="flex overflow-x-auto scrollbar-hide gap-1 p-1"
                  style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  {Object.entries(dynamicCategories).map(([key, category]) => (
                    <motion.button
                      key={key}
                      onClick={() => handleCategorySelect(key)}
                      className={cn(
                        'flex-shrink-0 flex flex-col items-center gap-1 p-2 rounded-lg outline-none transition-all duration-200 min-w-10',
                        selectedCategory === key ? 'bg-blue-100 border-blue-500 shadow-sm' : 'hover:bg-gray-100'
                      )}
                      title={category.name}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {renderCategoryIcon(category, key)}
                      <div
                        className={cn(
                          'w-full h-1 rounded-full transition-colors',
                          selectedCategory === key ? 'bg-blue-500' : 'bg-transparent'
                        )}
                      />
                    </motion.button>
                  ))}
                </div>
              </div>

              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'h-10 w-8 p-0 flex-shrink-0 transition-all duration-200',
                  canScrollRight ? 'hover:bg-gray-200 text-gray-700' : 'opacity-30 cursor-not-allowed text-gray-400'
                )}
                onClick={scrollCategoryRight}
                disabled={!canScrollRight}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Emoji Grid */}
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="p-3">{renderEmojiGrid()}</div>
            </ScrollArea>
          </div>

          {/* Footer */}
          <div className="px-3 py-2 text-xs text-gray-500 border-t bg-gray-50 flex items-center justify-between">
            <span className="text-blue-500">Use ← → keys</span>
            <span className="font-medium">
              {selectedCategory === 'memes'
                ? `${customEmojis.length} memes`
                : `${currentCategoryIndex + 1} of ${categoryKeys.length}`}
            </span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { EmojiPicker }
